import { Box, List } from "@mui/material";
import Header from "../header/header";
import { useEffect, useState } from "react";
import Dashboard from "../dashboard/dashboard";
import { loadClients } from "../../store/slices/clientSlice";
import apiClient from "../../axiosClient";
import Client from "../../interfaces/client";
import { AppDispatch, RootState } from "../../store/store";
import { useAppDispatch } from "../../store/hooks";
import FieldService from "../../services/fieldService";
import ManageField from "../manageFields/manageField";
import ManageSettlementRule from "../manageSettlementRule/manageSettlementRule";
import { loadPicklist } from "../../store/slices/picklistSlice";
import Picklist from "../../interfaces/picklist";
import RoleManagement from "../../security/roleManagement";
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageClientIcon from '@mui/icons-material/PeopleAlt';
import ManageAgentIcon from '@mui/icons-material/SupportAgent';
import ManageUsersIcon from '@mui/icons-material/People';
import ManageFieldIcon from '@mui/icons-material/Storage';
import ManageFieldAliasIcon from '@mui/icons-material/MergeType';
import ManageSegmentIcon from '@mui/icons-material/Category';
import ManagePincodeIcon from '@mui/icons-material/Place';
import ManageSettlementRulesIcon from '@mui/icons-material/Gavel';
import AgentStatsIcon from '@mui/icons-material/QueryStats';
import ManageContractsIcon from '@mui/icons-material/BusinessCenter';
import MyAllocationIcon from '@mui/icons-material/AssignmentInd';
import FocusedCasesIcon from '@mui/icons-material/Star';
import ManageAllocationIcon from '@mui/icons-material/SwapHoriz';
import ManageDispositionsIcon from '@mui/icons-material/AccountTree';
import ManagePermissionSetIcon from '@mui/icons-material/PlaylistAddCheck';
import ManagePermissionIcon from '@mui/icons-material/VpnKey';
import ManageEntityPermissionIcon from '@mui/icons-material/ManageAccounts';
import ManageDialerPermissionIcon from '@mui/icons-material/ManageAccounts';
import DialerIcon from '@mui/icons-material/Tty';
import CampaignIcon from '@mui/icons-material/Campaign';
import './homepage.css';
import { useNavigate } from "react-router-dom";
import AgentStatsBoard from "../agentStatsBoard/agentStatsBoard";
import { loadClientManagerDescendants, loadUsers } from "../../store/slices/userSlice";
import User, { UserStatus } from "../../interfaces/user";
import { decodedJWT, getCurrentPointerTimestamp, getOwnerName, getOwnerType, getPdbID, getUserName, getUserRole, sendLoginAudit, setCurrentPointerLocalStorage, setCurrentPointerTimestamp } from "../../utils/commonUtils";
import InteractionAuditTable from "../interaction_audit/interactionAuditTable";
import InteractionAuditIcon from '@mui/icons-material/FactCheck';
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import ManageUsers from "../ManageUsers/manageUsers";
import ManageClient from "../manageClient/manageClient";
import { loadAgencies } from "../../store/slices/agencySlice";
import Agency from "../../interfaces/agency";
import ManageAgency from "../manageAgency/manageAgency";
import ManageSegments from "../manageSegment/manageSegment";
import { loadSegments } from "../../store/slices/segmentSlice";
import { loadDistricts } from "../../store/slices/districtAndStateSlice";
import ManagePincodes from "../managePincode/managePincodes";
import ManageContracts from "../manageContracts/manageContracts";
import NavMenuComponent from "../baseComponents/navMenuComponent";
import FocusedCases from "../dashboard/focusedCases";
import MyAllocation from "../dashboard/myAllocation";
import ManageAllocation from "../dashboard/manageAllocation";
import ManageDispositions from "../ManageDispositions/manageDispositions";
import { loadPermissionSet } from "../../store/slices/permissionSetSlice";
import PermissionSet from "../../interfaces/permissionSet";
import ManageFieldAlias from "../manageFields/manageFieldAlias";
import ManagePermissionSet from "../ManagePermissionSet/managePermissionSet";
import { loadPermissions } from "../../store/slices/permissionsSlice";
import ManagePermissions from "../managePermissions/managePermissions";
import ManageEntityPermissions from "../manageEntityPermissions/manageEntityPermissions";
import { loadEntityPermissions } from "../../store/slices/entityPermissionsSlice";
import { loadFocusedCaseAllocation } from "../../store/slices/focusedCaseAllocationSlice";
import { setDialerVisibility, setTCNAgentDetails } from "../../store/slices/dialerStateSlice";
import { useSelector } from "react-redux";
import TCNDialerComponent from "../dialer/tcnDialerComponent";
import NavMenuComponentLight from "../baseComponents/navMenuComponentLight";
import PermissionManagement from "../../permissions/permissionManagement";
import { useBroadcastNavigation } from "../../utils/useBroadcastNavigation";
import Campaign from "../campaign/campaign";
import ManageDialerConfig from "../manageDialerConfig/manageDialerConfig";

type NavigationTabList = {
	name: string;
	isVisible: boolean;
	icon: React.ReactNode;
	tabNumber: number;
};
const HomePage: React.FC = () => {
	useBroadcastNavigation();
	const navigate = useNavigate();
	const token: JWTTokenPayload | false = decodedJWT();
	const userRole = getUserRole(token);
	const ownerType = getOwnerType(token);
	const ownerName = getOwnerName(token);
	const pdbID = getPdbID(token);
	const defaultTab = RoleManagement.isSuperAdmin(userRole) ? 6 : RoleManagement.isAuditor(userRole) ? 5 : (RoleManagement.isAdmin(userRole) || RoleManagement.isSupervisor(userRole) || RoleManagement.isManager(userRole)) ? 0 : 12;
	const [selectedTab, setSelectedTab] = useState<number>(defaultTab);
	const isDialerVisible = useSelector((state: RootState) => state.dialerState.isDialerVisible);
	const isCampaignEnabled = useSelector((state: RootState) => PermissionManagement.isCampaignEnabled(state));
	const isUserManagementEnabled = useSelector((state: RootState) => PermissionManagement.isUserManagementEnabled(state));
	const dispatch: AppDispatch = useAppDispatch();

	const checkJWTTokenExpiration = () => {
		if (!token) {
			navigate('/login');
			return false;
		} else {
			const { exp } = token;
			const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
			if (exp < currentTime) {
				alert('Your Session has Expired \nPlease Login again');
				navigate('/login');
				return false;
			} else if (!pdbID) {
				alert('Portfolio is not selected\nPlease select a portfolio');
				navigate('/portfolio');
				return false;
			}
			return true;
		}
	};

	// Make API call to fetch dropdown keys on component load
	const fetchClients = async () => {
		try {
			const response = await apiClient.get('/api/v1/client');
			if (response.status === 200) {
				dispatch(loadClients(
					(response.data as Client[]).filter(val => RoleManagement.isAgency(ownerType) ? val.pdb_id === pdbID : true)
				));
			} else {
				alert("Error while loading client list. Error: " + response.data.message);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchAgencies = async () => {
		try {
			const response = await apiClient.get('/api/v1/agency');
			if (response.status === 200) {
				dispatch(loadAgencies(response.data as Agency[]));
			} else {
				alert("Error while loading agency list. Error: " + response.data.message);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchPicklist = async () => {
		try {
			const response = await apiClient.get('/api/v1/picklist');
			if (response.status === 200) {
				dispatch(loadPicklist(response.data as Picklist));
			} else {
				alert("Error while loading picklist. Error: " + response.data.message);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchCombinedFocusedCaseAllocationList = async () => {
		try {
			const response = await apiClient.get('/api/v1/agent_case_allocation/combined_focused_allocation');
			if (response.status === 200) {
				dispatch(loadFocusedCaseAllocation(response.data));
				const todaysTimestamp = new Date(Date.now() + 19800000).toISOString().split('T')[0];
				const currentPointerTimestamp = getCurrentPointerTimestamp();
				if (localStorage.getItem("currentPointer") === null || response.data.length === 0 || currentPointerTimestamp !== todaysTimestamp || !currentPointerTimestamp) {
					setCurrentPointerLocalStorage(0);
					setCurrentPointerTimestamp(todaysTimestamp);
				}
			} else {
				alert("Error while loading Todays Case List. Error " + response.data.message);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchUserList = async () => {
		try {
			if (!RoleManagement.isAgent(userRole) && !RoleManagement.isTeamLead(userRole)) {
				const query = RoleManagement.isSuperAdmin(userRole) ? '?user_role=Admin' : '';
				const response = await apiClient.get(`/api/v1/user${query}`);
				if (response.status === 200) {
					dispatch(loadUsers(response.data.user_responses as User[]));
				}
			} else {
				const response = await apiClient.get(`/api/v1/user/${getUserName(token)}`);
				if (response.status === 200) {
					if (response.data.status === UserStatus.APPROVED) {
						dispatch(loadUsers([response.data] as User[]));
					} else {
						alert('Your application is not approved, please contact your admin');
						navigate('/login');
					}
				}
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchSegments = async () => {
		if (RoleManagement.isAgency(ownerType)) {
			try {
				const response = await apiClient.get(`/api/v1/segment?agency_name=${ownerName}`);
				if (response.status === 200) {
					dispatch(loadSegments(response.data));
				}
			} catch (error: any) {
				if (error.response) {
					alert('Error response data:' + error.response.data.message);
				} else if (error.request) {
					alert('No response received:' + error.request);
				} else {
					alert('Error message:' + error.message);
				}
			}
		}
	};

	const fetchDistricts = async () => {
		try {
			const response = await apiClient.get(`/api/v1/pin_code/district`);
			if (response.status === 200) {
				dispatch(loadDistricts(response.data));
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchClientManagerDescendants = async () => {
		try {
			const response = await apiClient.get(`/api/v1/user/client_manager_descendant`);
			if (response.status === 200) {
				dispatch(loadClientManagerDescendants(response.data));
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchPermissionSet = async () => {
		try {
			const response = await apiClient.get('/api/v1/permission_set');
			if (response.status === 200) {
				const permissions = response.data as PermissionSet[];
				dispatch(loadPermissionSet(permissions));
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchPermissions = async () => {
		try {
			const response = await apiClient.get('/api/v1/permissions');
			if (response.status === 200) {
				dispatch(loadPermissions(response.data));
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchEntityPermissions = async () => {
		try {
			const response = await apiClient.get('/api/v1/entity_permissions');
			if (response.status === 200) {
				dispatch(loadEntityPermissions(response.data));
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

	const fetchTCNAgentDetails = async () => {
		try {
			const response = await apiClient.get('/api/v1/tcn_config/collectedge_tcn_agent_mapping?is_active=true&username=' + getUserName(token));
			if (response.status === 200) {
				if (response.data.length !== 0) {
					const agent = response.data[0];
					dispatch(setTCNAgentDetails(agent));
				}
			} else {
				alert("Error while loading tcn agent details. Error: " + response.data.message);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};

    const callAPIs = async () => {
        const loggedIn = checkJWTTokenExpiration();
        if (loggedIn) {
            await sendLoginAudit();
            if(RoleManagement.isAgency(ownerType)){
                // Access to all at agency side
                await fetchUserList();
                await fetchClients();
				await fetchPicklist();
                if (!RoleManagement.isAuditor(userRole)) {
                    await fetchDistricts();
                    await fetchCombinedFocusedCaseAllocationList();
                    await fetchSegments();
                    await FieldService.updateFieldsInRedux();
                    await fetchPermissionSet();
                    await fetchPermissions();
                    await fetchEntityPermissions();
					await fetchTCNAgentDetails();
                }
            }
            if(RoleManagement.isClient(ownerType)){
                // Access to all at client side
				await fetchUserList();
                await fetchAgencies();
				await fetchPicklist();
                if (!RoleManagement.isAuditor(userRole)) {
                    await fetchDistricts();
                    await FieldService.updateFieldsInRedux();
                    await fetchClientManagerDescendants();
                    await fetchPermissionSet();
                    await fetchPermissions();
                    await fetchEntityPermissions();
                }
            }
            if((RoleManagement.isSuperAdmin(userRole))){
                // Access to only Super Admin
                await fetchUserList();
                await FieldService.updateFieldsInRedux();
                await fetchAgencies();
                await fetchClients();
                await fetchDistricts();
                await fetchPicklist();
                await fetchPermissions();
            }
        }
    };

	useEffect(() => {
		callAPIs();
	}, []);

	const navigationTabsList: NavigationTabList[] = [
		{
			name: "Dashboard",
			isVisible: !RoleManagement.isSuperAdmin(userRole) && !RoleManagement.isAuditor(userRole) && !RoleManagement.isAgent(userRole),
			icon: <DashboardIcon fontSize="medium" />,
			tabNumber: 0
		},
		{
			name: "Agent Stats",
			isVisible: RoleManagement.isAgency(ownerType) && !RoleManagement.isAgent(userRole) && !RoleManagement.isAuditor(userRole),
			icon: <AgentStatsIcon fontSize="medium" />,
			tabNumber: 1
		},
		{
			name: "My Allocation",
			isVisible: RoleManagement.isAgency(ownerType) && !(RoleManagement.isAuditor(userRole) || RoleManagement.isAdmin(userRole)),
			icon: <MyAllocationIcon fontSize="medium" />,
			tabNumber: 12
		},
		{
			name: "Focused Cases",
			isVisible: RoleManagement.isAgency(ownerType) && !(RoleManagement.isAuditor(userRole) || RoleManagement.isAdmin(userRole)),
			icon: <FocusedCasesIcon fontSize="medium" />,
			tabNumber: 11
		},
		{
			name: "Manage Segments",
			isVisible: RoleManagement.isSupervisor(userRole) || (RoleManagement.isAdmin(userRole) && RoleManagement.isAgency(ownerType)),
			icon: <ManageSegmentIcon fontSize="medium" />,
			tabNumber: 9
		},
		{
			name: "Manage Allocation",
			isVisible: RoleManagement.isSupervisor(userRole) || (RoleManagement.isAdmin(userRole) && RoleManagement.isAgency(ownerType)),
			icon: <ManageAllocationIcon fontSize="medium" />,
			tabNumber: 13
		},
		{
			name: `Manage ${RoleManagement.isSuperAdmin(userRole) ? 'Admins' : 'Users'}`,
			isVisible: RoleManagement.isAdmin(userRole) || RoleManagement.isSuperAdmin(userRole) || (RoleManagement.isSupervisor(userRole) && isUserManagementEnabled),
			icon: <ManageUsersIcon fontSize="medium" />,
			tabNumber: 2
		},
		{
			name: "Manage Fields",
			isVisible: (RoleManagement.isSuperAdmin(userRole) || (RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType))),
			icon: <ManageFieldIcon fontSize="medium" />,
			tabNumber: 3
		},
		{
			name: "Manage Field Alias",
			isVisible: RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType),
			icon: <ManageFieldAliasIcon fontSize="medium" sx={{ transform: 'rotate(90deg)' }} />,
			tabNumber: 20,
		},
		{
			name: "Interactions Audit",
			isVisible: !RoleManagement.isAgent(userRole) && !RoleManagement.isTeamLead(userRole) && !RoleManagement.isSuperAdmin(userRole),
			icon: <InteractionAuditIcon fontSize="medium" />,
			tabNumber: 5
		},
		{
			name: "Manage Client",
			isVisible: RoleManagement.isSuperAdmin(userRole),
			icon: <ManageClientIcon fontSize="medium" />,
			tabNumber: 6
		},
		{
			name: "Manage Agency",
			isVisible: RoleManagement.isSuperAdmin(userRole),
			icon: <ManageAgentIcon fontSize="medium" />,
			tabNumber: 7
		},
		{
			name: "Manage Pincode",
			isVisible: RoleManagement.isSuperAdmin(userRole),
			icon: <ManagePincodeIcon fontSize="medium" />,
			tabNumber: 10
		},
		{
			name: "Manage Dispositions",
			isVisible: RoleManagement.isClient(ownerType) && RoleManagement.isAdmin(userRole),
			icon: <ManageDispositionsIcon fontSize="medium" />,
			tabNumber: 15
		},
		{
			name: "Manage Permission Set",
			isVisible: RoleManagement.isAdmin(userRole),
			icon: <ManagePermissionSetIcon fontSize="medium" />,
			tabNumber: 16
		},
		{
			name: "Manage Permissions",
			isVisible: RoleManagement.isSuperAdmin(userRole),
			icon: <ManagePermissionIcon fontSize="medium" />,
			tabNumber: 17
		},
		{
			name: "Manage Entity Permissions",
			isVisible: RoleManagement.isSuperAdmin(userRole),
			icon: <ManageEntityPermissionIcon fontSize="medium" />,
			tabNumber: 18
		},
		{
			name: "Campaign",
			isVisible: isCampaignEnabled,
			icon: <CampaignIcon fontSize="medium" />,
			tabNumber: 21
		},
		{
			name: "Manage Contracts",
			isVisible: RoleManagement.isSuperAdmin(userRole) || (RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType)),
			icon: <ManageContractsIcon fontSize="medium" />,
			tabNumber: 14
		},
		{
			name: "Manage Settlement Rules",
			isVisible: RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType),
			icon: <ManageSettlementRulesIcon fontSize="medium" />,
			tabNumber: 4
		},
		{
			name: "Dialer Configuration",
			isVisible: RoleManagement.isSuperAdmin(userRole),
			icon: <ManageDialerPermissionIcon fontSize="medium" />,
			tabNumber: 22
		}
		// Agent mapping with TCN agent Id's
	];

	return (
		<Box m={2} sx={{ height: '100vh', width: '100vw', margin: 0 }}>

			<Header />
			<div style={{ display: "flex", flexDirection: "row", height: 'calc(100vh - 84px)' }}>
				<div style={{ justifyContent: 'space-between' }} className="design-system-editor-left-nav" >
					<div>
						<List>
							{navigationTabsList.map((val, index) => {
								return val.isVisible && (
									<NavMenuComponent
										key={index}
										text={val.name}
										selectedTab={selectedTab}
										setSelectedTab={setSelectedTab}
										icon={val.icon}
										tabNumber={val.tabNumber}
									/>
								);
							})}
						</List>
					</div>
					<div>
						{PermissionManagement.isDialerEnabled(useSelector((state: RootState) => state))
							&& <NavMenuComponentLight
								key="dialer-toggle"
								text="Dialer"
								icon={<DialerIcon fontSize="medium" />}
								setSelected={(value: boolean) => dispatch(setDialerVisibility(value))}
								selected={isDialerVisible}
							/>}
					</div>
				</div>
				<div className="design-system-editor-right-content">
					{isDialerVisible && <TCNDialerComponent />}
					<div>
						{selectedTab === 0 && <Dashboard />}
						{selectedTab === 1 && <AgentStatsBoard />}
						{selectedTab === 2 && <ManageUsers />}
						{selectedTab === 3 && <ManageField />}
						{selectedTab === 4 && <ManageSettlementRule />}
						{selectedTab === 5 && <InteractionAuditTable />}
						{selectedTab === 6 && <ManageClient />}
						{selectedTab === 7 && <ManageAgency />}
						{selectedTab === 9 && <ManageSegments />}
						{selectedTab === 10 && <ManagePincodes />}
						{selectedTab === 11 && <FocusedCases />}
						{selectedTab === 12 && <MyAllocation />}
						{selectedTab === 13 && <ManageAllocation />}
						{selectedTab === 14 && <ManageContracts />}
						{selectedTab === 15 && <ManageDispositions />}
						{selectedTab === 16 && <ManagePermissionSet />}
						{selectedTab === 17 && <ManagePermissions />}
						{selectedTab === 18 && <ManageEntityPermissions />}
						{selectedTab === 20 && <ManageFieldAlias />}
						{selectedTab === 21 && <Campaign />}
						{selectedTab === 22 && <ManageDialerConfig/>}
					</div>
				</div>
			</div>
		</Box>
	);
}

export default HomePage;